import { createRng } from './deterministicRng';

/**
 * Deterministic shuffle utility for question renderers
 *
 * Uses question.id as seed to ensure consistent shuffle across renders
 * while still providing randomization per question.
 */

export interface ShuffleResult<T> {
  shuffled: T[];
  originalIndices: number[];
}

/**
 * Hash a string to a numeric seed
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i) | 0;
  }
  return hash >>> 0; // Ensure unsigned 32-bit
}

/**
 * Shuffle an array deterministically based on a seed string
 *
 * @param items - Array to shuffle
 * @param seed - Seed string (typically question.id)
 * @returns Shuffled array with original indices preserved
 */
export function shuffleWithSeed<T>(items: T[], seed: string): ShuffleResult<T> {
  const numericSeed = hashString(seed);
  const rng = createRng(numericSeed);

  // Create array with original indices
  const indexed = items.map((item, index) => ({
    item,
    originalIndex: index
  }));

  // Fisher-Yates shuffle with deterministic RNG
  for (let i = indexed.length - 1; i > 0; i--) {
    const j = Math.floor(rng.random() * (i + 1));
    [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
  }
  return {
    shuffled: indexed.map(x => x.item),
    originalIndices: indexed.map(x => x.originalIndex)
  };
}

/**
 * Shuffle options for multiple choice questions
 * Preserves original indices for answer validation
 */
export function shuffleOptions(options: string[], questionId: string): Array<{
  text: string;
  originalIndex: number;
}> {
  const result = shuffleWithSeed(options, questionId);
  return result.shuffled.map((text, idx) => ({
    text,
    originalIndex: result.originalIndices[idx]
  }));
}